import { useState, useEffect, useCallback } from 'react';
import { apiClient, type ChallengeDetailResponse, type ApiError } from '@/services/api-client';

interface UseChallengeDetailResult {
  challengeDetail: ChallengeDetailResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch challenge details including averageSolve statistic
 * @param gameId - The ID of the game
 * @param challengeId - The ID of the challenge
 * @returns Challenge detail data, loading state, error state, and refetch function
 */
export function useChallengeDetail(
  gameId: string | null,
  challengeId: string | null
): UseChallengeDetailResult {
  const [challengeDetail, setChallengeDetail] = useState<ChallengeDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchChallengeDetail = useCallback(async () => {
    if (!gameId || !challengeId) {
      setChallengeDetail(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const detail = await apiClient.getChallengeDetail(gameId, challengeId);
      setChallengeDetail(detail);
    } catch (err) {
      const apiError = err as ApiError;
      const errorMessage = apiError.message || 'Failed to fetch challenge detail';
      setError(new Error(errorMessage));
      setChallengeDetail(null);
    } finally {
      setLoading(false);
    }
  }, [gameId, challengeId]);

  useEffect(() => {
    fetchChallengeDetail();
  }, [fetchChallengeDetail]);

  return {
    challengeDetail,
    loading,
    error,
    refetch: fetchChallengeDetail
  };
}